/**
 * Chat Export Utilities
 * Functions for extracting and exporting tables from chat messages
 */

/**
 * Extracts all tables from HTML string
 * @param {string} html - HTML string containing table elements
 * @returns {Array} Array of table objects with headers and rows
 */
export const extractTablesFromHTML = (html) => {
    if (!html) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const tables = doc.querySelectorAll('table');

    return Array.from(tables).map((table, index) => {
        const headers = [];
        const rows = [];

        // Extract headers from thead or first row
        const headerCells = table.querySelectorAll('thead th, thead td');
        if (headerCells.length > 0) {
            headerCells.forEach(cell => {
                headers.push(cell.textContent.trim());
            });
        } else {
            // If no thead, try first row
            const firstRow = table.querySelector('tr');
            if (firstRow) {
                const cells = firstRow.querySelectorAll('th, td');
                cells.forEach(cell => {
                    headers.push(cell.textContent.trim());
                });
            }
        }

        // Extract data rows from tbody or all rows
        const dataRows = table.querySelectorAll('tbody tr');
        const rowsToProcess = dataRows.length > 0 ? dataRows : table.querySelectorAll('tr');

        rowsToProcess.forEach((row, rowIndex) => {
            // Skip first row if it was used as header and no thead exists
            if (headerCells.length === 0 && rowIndex === 0) return;

            const cells = row.querySelectorAll('td, th');
            if (cells.length > 0) {
                const rowData = [];
                cells.forEach(cell => {
                    rowData.push(cell.textContent.trim());
                });
                rows.push(rowData);
            }
        });

        return {
            index,
            headers,
            rows,
            totalRows: rows.length,
            totalColumns: headers.length || (rows[0]?.length || 0)
        };
    });
};

/**
 * Checks if HTML string contains tables
 * @param {string} html - HTML string to check
 * @returns {boolean} True if tables exist
 */
export const hasTable = (html) => {
    if (!html) return false;
    return html.includes('<table') || html.includes('</table>');
};

/**
 * Converts 2D array to CSV string
 * @param {Array} data - 2D array of data
 * @returns {string} CSV formatted string
 */
const arrayToCSV = (data) => {
    return data.map(row => {
        return row.map(cell => {
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
        }).join(',');
    }).join('\n');
};

/**
 * Exports a single table to CSV file
 * @param {Object} tableData - Table object with headers and rows
 * @param {string} filename - Name for the downloaded file
 */
export const exportTableToCSV = (tableData, filename = 'table-export.csv') => {
    if (!tableData || !tableData.rows || tableData.rows.length === 0) {
        console.warn('No table data to export');
        return;
    }

    // Combine headers and rows
    const csvData = [];
    if (tableData.headers && tableData.headers.length > 0) {
        csvData.push(tableData.headers);
    }
    csvData.push(...tableData.rows);

    // Convert to CSV string
    const csvContent = arrayToCSV(csvData);

    // Add UTF-8 BOM to ensure proper encoding in Excel and other programs
    const BOM = '\uFEFF';
    const csvContentWithBOM = BOM + csvContent;

    // Create blob and download
    const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Exports all tables from HTML to separate CSV files
 * @param {string} html - HTML string containing tables
 * @param {string} baseFilename - Base name for downloaded files
 */
export const exportAllTablesFromHTML = (html, baseFilename = 'chat-table') => {
    const tables = extractTablesFromHTML(html);

    if (tables.length === 0) {
        console.warn('No tables found in HTML');
        return;
    }

    tables.forEach((table, index) => {
        const filename = tables.length > 1
            ? `${baseFilename}-${index + 1}.csv`
            : `${baseFilename}.csv`;
        exportTableToCSV(table, filename);
    });
};

/**
 * Generates a filename based on message timestamp
 * @param {Object} message - Message object with timestamp
 * @param {number} tableIndex - Index of table in message
 * @returns {string} Generated filename
 */
export const generateTableFilename = (message, tableIndex = 0) => {
    const timestamp = message?.timestamp
        ? new Date(message.timestamp).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

    return tableIndex > 0
        ? `table-${timestamp}-${tableIndex + 1}.csv`
        : `table-${timestamp}.csv`;
};
